/**
 * Utility functions for formatting a timestamp (i.e. a single instant in time).
 *
 * Expected inputs are:
 * - the timestamp itself as an epoch milliseconds value
 * - the named format type to use (see `SupportedTimeFormat` below)
 * - locale info, which includes:
 *     - the time zone to use for display
 *
 * The `formatTimestamp` function can be used to just get a formatted string.
 *
 * The `formatTimestampAsParts` function returns a richer list of objects that separate
 * the date/time/timeZone portion of the formatted output and include screen-reader-friendly labels.
 *
 * This approach uses a polyfill for the upcoming [Temporal API](https://tc39.es/proposal-temporal/docs/index.html) and
 * the relatively new [Intl.DateTimeFormat API](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat)
 * as a way to work directly with the browser to handle/format dates, instead of delegating that to a library.
 *
 */

import { last } from 'lodash';
import { Temporal } from 'temporal-polyfill';
import stringFormatObject from 'string-format-obj';
import moment from '@splunk/moment';

import {
    centuryLabels,
    dayLabels,
    decadeLabels,
    digitLabels,
    hourLabels,
    twelveHourLabels,
    minuteLabels,
    secondLabels,
} from './timeAriaLabels';

export type SupportedTimeFormat =
    | 'date'
    | 'dateCompact'
    | 'dateVerbose'
    | 'time'
    | 'timeCompact'
    | 'timePrecise'
    | 'timeExtraPrecise'
    | 'dateTime'
    | 'dateTimeNoTimezone'
    | 'dateCompactTime'
    | 'dateTimeCompact'
    | 'dateCompactTimeCompact'
    | 'dateTimePrecise'
    | 'dateTimePreciseNoTimezone'
    | 'dateTimeExtraPrecise'
    | 'dateVerboseTime'
    | 'dateVerboseTimePrecise'
    | 'dateVerboseTimeExtraPrecise'
    | 'isoDate'
    | 'isoTime'
    | 'isoLocal'
    | 'isoWithOffset';

// assume the maximum value an epoch seconds value could be as 10x the current value,
// anything greater will be interpreted as an epoch miliseconds value
const EPOCH_SECONDS_MAX_VALUE = Date.now() / 100;

const TWELVEHOUR = {
    AM: 'AM',
    PM: 'PM',
};

type FormatPartInput = {
    name: 'time' | 'date' | 'timeZone' | 'dateTime';
    valueFormatString: string;
    labelFormatString: string;
};

type FormatPartOutput = {
    name: 'time' | 'date' | 'timeZone' | 'dateTime';
    value: string;
    label: string;
};

type formatRangeOutput = {
    from: FormatPartOutput[];
    to: FormatPartOutput[];
    fromString: string;
    toString: string;
};

const datePartInput: FormatPartInput = {
    name: 'date',
    valueFormatString: '{day} {month} {year}',
    labelFormatString: '{month} {day} {year}',
};

const dateCompactPartInput: FormatPartInput = {
    name: 'date',
    valueFormatString: '{day} {month}',
    labelFormatString: '{month} {day}',
};

const dateVerbosePartInput: FormatPartInput = {
    name: 'date',
    valueFormatString: '{weekday} {day} {month} {year}',
    labelFormatString: '{weekday} {month} {day} {year}',
};

const timePartInput: FormatPartInput = {
    name: 'time',
    valueFormatString: '{hour}:{minute}{meridiem}',
    labelFormatString: '{hour} {minute}{meridiem}',
};

const timePrecisePartInput: FormatPartInput = {
    name: 'time',
    valueFormatString: '{hour}:{minute}:{second}{meridiem}',
    labelFormatString: '{hour} {minute} and {second} seconds{meridiem}',
};

const timeExtraPrecisePartInput: FormatPartInput = {
    name: 'time',
    valueFormatString: '{hour}:{minute}:{second}.{fractionalSecond}{meridiem}',
    labelFormatString: '{hour} {minute} and {second} point {fractionalSecond} seconds{meridiem}',
};

const timezonePartInput: FormatPartInput = {
    name: 'timeZone',
    valueFormatString: '{timeZoneName}',
    labelFormatString: '{timeZoneName}',
};

// For each named format type, map it to a list of "format parts", which contain:
//   - a name to denote which component of the timestamp it represents (e.g. "time" or "date" or "timeZone").
//   - a format string for the value that will be displayed to the user
//   - a format string for the label that will be used for screen-reader text
const formatPartsMap: Record<SupportedTimeFormat, FormatPartInput[]> = {
    date: [datePartInput],
    dateCompact: [dateCompactPartInput],
    dateVerbose: [dateVerbosePartInput],
    time: [timePartInput, timezonePartInput],
    timeCompact: [timePartInput],
    timePrecise: [timePrecisePartInput, timezonePartInput],
    timeExtraPrecise: [timeExtraPrecisePartInput, timezonePartInput],
    dateTime: [datePartInput, timePartInput, timezonePartInput],
    dateTimeNoTimezone: [datePartInput, timePartInput],
    dateCompactTime: [dateCompactPartInput, timePartInput, timezonePartInput],
    dateTimeCompact: [datePartInput, timePartInput],
    dateCompactTimeCompact: [dateCompactPartInput, timePartInput],
    dateTimePrecise: [datePartInput, timePrecisePartInput, timezonePartInput],
    dateTimePreciseNoTimezone: [datePartInput, timePrecisePartInput],
    dateTimeExtraPrecise: [datePartInput, timeExtraPrecisePartInput, timezonePartInput],
    dateVerboseTime: [dateVerbosePartInput, timePartInput, timezonePartInput],
    dateVerboseTimePrecise: [dateVerbosePartInput, timePrecisePartInput, timezonePartInput],
    dateVerboseTimeExtraPrecise: [dateVerbosePartInput, timeExtraPrecisePartInput, timezonePartInput],
    isoDate: [
        {
            name: 'date',
            valueFormatString: '{year}-{monthNumeric}-{dayPadded}',
            labelFormatString: '',
        },
    ],
    isoTime: [
        {
            name: 'time',
            valueFormatString: '{hourPadded}:{minute}:{second}',
            labelFormatString: '',
        },
    ],
    isoLocal: [
        {
            name: 'dateTime',
            valueFormatString:
                '{year}-{monthNumeric}-{dayPadded}T{hourPadded}:{minute}:{second}.{fractionalSecond}',
            labelFormatString: '',
        },
    ],
    isoWithOffset: [
        {
            name: 'dateTime',
            valueFormatString:
                '{year}-{monthNumeric}-{dayPadded}T{hourPadded}:{minute}:{second}.{fractionalSecond}{timeZoneOffset}',
            labelFormatString: '',
        },
    ],
};

export type LocaleInfo = {
    tz?: string;
    ianaTimezone?: string;
    serializedTimezone?: string;
    utcOffset?: number;
    /**
     * Denotes whether to display time with 12 hour time format. Preference can be used by passing in
     * "sf_12hourTimeFormat" from current user service. When not passed, 24 hour time is used by default.
     */
    twelveHourTimeFormat?: boolean;
};

const todayPart: FormatPartOutput = { name: 'date', label: 'today', value: 'Today' };
const yesterdayPart: FormatPartOutput = { name: 'date', label: 'yesterday', value: 'Yesterday' };

// Converts a ZonedDateTime into an object whose keys are used for substition in the `valueFormatString`
// when computing a format part. If `twelveHourTimeFormat` is true, the value object will be in 12 hour time
// and an AM/PM meridiem will be added. Otherwise, time will be formatted in 24 hour time.
const zdtToValueObject = (
    zdt: Temporal.ZonedDateTime,
    twelveHourTimeFormat?: boolean
): Record<string, string> => {
    const fmt = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        timeZone: zdt.timeZone.toString(),
        hourCycle: 'h23',
    });
    // computing the time zone name should be done without specifying a locale (i.e. using the system locale),
    // since the locale will decide whether to use a local name for that time zone (e.g. "PDT")
    // or just show it with an offset (e.g. "GMT-7"). The `split` logic is a workaround for the
    // fact that DateTimeFormat can't be used to *only* return a time zone name.
    const timeZoneName =
        last(
            Intl.DateTimeFormat(undefined, {
                timeZoneName: 'short',
                timeZone: zdt.timeZone.toString(),
            })
                .format(zdt.epochMilliseconds)
                .split(' ')
        ) || '';
    const parts = fmt.formatToParts(zdt.epochMilliseconds);
    const obj = Object.fromEntries(parts.map(part => [part.type, part.value]));

    // calculate hour and meridem for 12 hour time
    const hour = twelveHourTimeFormat ? String(zdt.hour % 12 || 12) : obj.hour;
    const twelveFormatMeridiem = ` ${zdt.hour >= 12 ? TWELVEHOUR.PM : TWELVEHOUR.AM}`;
    return {
        ...obj,
        timeZoneName,
        hour,
        hourPadded: obj.hour,
        dayPadded: parseInt(obj.day, 10) > 9 ? obj.day : `0${obj.day}`,
        monthNumeric: zdt.month > 9 ? String(zdt.month) : `0${zdt.month}`,
        meridiem: twelveHourTimeFormat ? twelveFormatMeridiem : '',
        timeZoneOffset: zdt.offset,
    };
};

// Converts a ZonedDateTime into an object whose keys are used for substition in the `labelFormatString`
// when computing a format part. If `twelveHourTimeFormat` is true, the label object will be in 12 hour time
// and an AM/PM meridiem will be added. Otherwise, time will be formatted in 24 hour time.
const zdtToLabelObject = (
    zdt: Temporal.ZonedDateTime,
    twelveHourTimeFormat?: boolean
): Record<string, string> => {
    const fmt = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        fractionalSecondDigits: 3,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long',
        timeZone: zdt.timeZone.toString(),
        timeZoneName: 'long',
        hourCycle: 'h23',
    });
    const parts = fmt.formatToParts(zdt.epochMilliseconds);
    const obj = Object.fromEntries(parts.map(part => [part.type, part.value]));

    // calculate hour and meridem for 12 hour time
    const hour = twelveHourTimeFormat ? twelveHourLabels[zdt.hour % 12 || 12] : hourLabels[Number(obj.hour)];
    const twelveFormatMeridiem = ` ${zdt.hour >= 12 ? TWELVEHOUR.PM : TWELVEHOUR.AM}`;

    return {
        ...obj,
        second: secondLabels[Number(obj.second)],
        minute: minuteLabels[Number(obj.minute)],
        hour,
        day: dayLabels[Number(obj.day)],
        meridiem: twelveHourTimeFormat ? twelveFormatMeridiem : '',
        year: `${centuryLabels[Number(obj.year.slice(0, 2))]} ${decadeLabels[Number(obj.year.slice(2, 4))]}`,
        fractionalSecond: Array.from(obj.fractionalSecond)
            .map(digit => digitLabels[Number(digit)])
            .join(' '),
    };
};

const momentToValueObject = (m: moment.Moment, twelveHour = true) => ({
    day: m.format('D'),
    dayPadded: m.format('DD'),
    fractionalSecond: m.format('SSS'),
    hour: twelveHour ? m.format('h') : m.format('H'),
    hourPadded: twelveHour ? m.format('hh') : m.format('HH'),
    literal: '.', // matches literal: "." from zdtToLabelObject
    meridiem: twelveHour ? ` ${m.format('A')}` : '',
    minute: m.format('mm'),
    second: m.format('ss'),
    month: m.format('MMM'), // matches short month name like "Mar"
    monthNumeric: m.format('MM'),
    timeZoneName: m.zoneAbbr() || m.format('z'),
    timeZoneOffset: m.format('Z'),
    weekday: m.format('ddd'), // matches short weekday name like "Tue"
    year: m.format('YYYY'),
});

export const formatTimestamp = (
    ts: number,
    formatName: SupportedTimeFormat,
    localeInfo: LocaleInfo
): string => {
    const tsAsMillis = Math.abs(ts) <= EPOCH_SECONDS_MAX_VALUE ? ts * 1000 : ts;
    let valueObj: Record<string, string>;
    // for splunk provider provided tz use @splunk/moment to process instead of Temporal
    if (localeInfo.serializedTimezone || localeInfo.ianaTimezone || localeInfo.utcOffset) {
        let momentDate: moment.Moment;

        if (localeInfo.serializedTimezone) {
            const tz = moment.setDefaultSplunkTimezone(localeInfo.serializedTimezone);
            momentDate = moment.tz(tsAsMillis, tz);
        } else if (localeInfo.ianaTimezone) {
            momentDate = moment.tz(tsAsMillis, localeInfo.ianaTimezone);
        } else {
            momentDate = moment.utc(tsAsMillis).utcOffset(localeInfo.utcOffset);
        }
        valueObj = momentToValueObject(momentDate, localeInfo.twelveHourTimeFormat);
    } else if (localeInfo.tz) {
        // default to Temporal if simple tz string is provided
        const tsAsInstant = Temporal.Instant.fromEpochMilliseconds(tsAsMillis);
        const zdt = tsAsInstant.toZonedDateTimeISO(localeInfo.tz);
        valueObj = zdtToValueObject(zdt, localeInfo.twelveHourTimeFormat);
    } else {
        // if no tz provided default to utc moment
        const momentDate = moment.utc(tsAsMillis);
        valueObj = momentToValueObject(momentDate, localeInfo.twelveHourTimeFormat);
    }

    const formatString = formatPartsMap[formatName].map(part => part.valueFormatString).join(' ');
    return stringFormatObject(formatString, valueObj);
};

export const formatTimestampAsParts = (
    ts: number,
    formatName: SupportedTimeFormat,
    localeInfo: LocaleInfo
): FormatPartOutput[] => {
    const tsAsInstant = Temporal.Instant.fromEpochMilliseconds(ts);
    const tsAsZdt = tsAsInstant.toZonedDateTimeISO(localeInfo.tz);
    const inputParts = formatPartsMap[formatName];
    const formatObj = zdtToValueObject(tsAsZdt, localeInfo.twelveHourTimeFormat);
    const labelObj = zdtToLabelObject(tsAsZdt, localeInfo.twelveHourTimeFormat);
    return inputParts.map(part => ({
        name: part.name,
        value: stringFormatObject(part.valueFormatString, formatObj),
        label: stringFormatObject(part.labelFormatString, labelObj),
    }));
};

export const tsToZdt = (ts: number, localeInfo: LocaleInfo): Temporal.ZonedDateTime => {
    const tsAsInstant = Temporal.Instant.fromEpochMilliseconds(ts);
    return tsAsInstant.toZonedDateTimeISO(localeInfo.tz);
};

export const currentTimeInTimezone = (locale: LocaleInfo): Temporal.ZonedDateTime =>
    Temporal.Now.zonedDateTimeISO(locale.tz);

const isSameYear = (fromZdt: Temporal.ZonedDateTime, toZdt: Temporal.ZonedDateTime): boolean =>
    fromZdt.year === toZdt.year;

const isSameDay = (fromZdt: Temporal.ZonedDateTime, toZdt: Temporal.ZonedDateTime): boolean =>
    isSameYear(fromZdt, toZdt) && fromZdt.month === toZdt.month && fromZdt.day === toZdt.day;

const isToday = (zdt: Temporal.ZonedDateTime, locale: LocaleInfo): boolean => {
    const todayZdt = Temporal.Now.zonedDateTimeISO(locale.tz);
    return isSameDay(todayZdt, zdt);
};

const isYesterday = (zdt: Temporal.ZonedDateTime, locale: LocaleInfo): boolean => {
    const todayZdt = Temporal.Now.zonedDateTimeISO(locale.tz);
    const oneDayAgoDuration = Temporal.Duration.from({ days: -1 });
    const yesterdayZdt = todayZdt.add(oneDayAgoDuration);
    return isSameDay(yesterdayZdt, zdt);
};

const isCurrentYear = (zdt: Temporal.ZonedDateTime, locale: LocaleInfo): boolean => {
    const todayZdt = Temporal.Now.zonedDateTimeISO(locale.tz);
    return isSameYear(todayZdt, zdt);
};

const formatRangeString = (zdt: Temporal.ZonedDateTime, parts: FormatPartOutput[]): string => {
    const formatString = parts.map(part => part.value).join(' ');
    const obj = zdtToValueObject(zdt);
    return stringFormatObject(formatString, obj);
};

export const formatTimestampRange = (
    fromTs: number,
    toTs: number,
    locale: LocaleInfo,
    formatName: SupportedTimeFormat
): formatRangeOutput => {
    const fromZdt = tsToZdt(fromTs, locale);
    const toZdt = tsToZdt(toTs, locale);

    let from = formatTimestampAsParts(fromTs, formatName, locale);
    let to = formatTimestampAsParts(toTs, formatName, locale);

    const foundTimeZone = formatPartsMap[formatName].find(part => part.name === 'timeZone');

    if (foundTimeZone) {
        from.pop();
    }

    // Special valid contractions.
    if (isToday(fromZdt, locale) && isToday(toZdt, locale)) {
        // Remove date and add `today` to `From`
        from.splice(0, 1);
        from.unshift(todayPart);
        to.splice(0, 1);
    } else if (isYesterday(fromZdt, locale) && isYesterday(toZdt, locale)) {
        // Remove date and add `yesterday` to `From`
        from.splice(0, 1);
        from.unshift(yesterdayPart);
        to.splice(0, 1);
    } else if (isSameYear(fromZdt, toZdt)) {
        // from `To` remove year from date.
        const inputParts = formatPartsMap[formatName].map(part => {
            if (part.valueFormatString === '{day} {month} {year}') {
                return dateCompactPartInput;
            }
            return part;
        });

        to = inputParts.map(part => ({
            name: part.name,
            value: stringFormatObject(
                part.valueFormatString,
                zdtToValueObject(toZdt, locale.twelveHourTimeFormat)
            ),
            label: stringFormatObject(
                part.labelFormatString,
                zdtToLabelObject(toZdt, locale.twelveHourTimeFormat)
            ),
        }));

        if (isCurrentYear(fromZdt, locale)) {
            from = inputParts.map(part => ({
                name: part.name,
                value: stringFormatObject(
                    part.valueFormatString,
                    zdtToValueObject(fromZdt, locale.twelveHourTimeFormat)
                ),
                label: stringFormatObject(
                    part.labelFormatString,
                    zdtToLabelObject(fromZdt, locale.twelveHourTimeFormat)
                ),
            }));
            const foundfromTimeZone = formatPartsMap[formatName].find(part => part.name === 'timeZone');
            if (foundfromTimeZone) {
                from.pop();
            }
        }
        if (isSameDay(fromZdt, toZdt)) {
            to.splice(0, 1);
        }
    } else if (isSameDay(fromZdt, toZdt)) {
        // from `To` remove date.
        to.splice(0, 1);
    }

    return {
        from,
        to,
        fromString: formatRangeString(fromZdt, from),
        toString: formatRangeString(toZdt, to),
    };
};
